import classnames from 'classnames';

import { LocationIcon, SuccessIcon } from '../../../../components/Icon';
import { ProgressTrackerSubStepStatus } from './types';

import './ProgressTracker.css';

export { ProgressTrackerSubStepStatus } from './types';

export interface ProgressTrackerProps {
  /**
   * Index of the currently active sub-step (0-based)
   */
  currentSubStepIndex: number;
  /**
   * Total number of sub-steps in the tracker
   */
  totalSubSteps: number;
  /**
   * Set of indices for completed sub-steps
   */
  completedSubStepIndices: Set<number>;
  visitedSubStepIndices?: Set<number>;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Callback when a sub-step is clicked
   */
  onSubStepClick?: (index: number) => void;
  /**
   * When true, all sub-steps are clickable regardless of completion status (useful for dev/testing)
   * @default false
   */
  allSubStepsClickable?: boolean;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

export function ProgressTracker({
  currentSubStepIndex,
  totalSubSteps,
  completedSubStepIndices,
  visitedSubStepIndices,
  className = '',
  onSubStepClick,
  allSubStepsClickable = false,
  'data-testid': dataTestId,
}: ProgressTrackerProps) {
  const baseTestId = dataTestId ?? 'progress-tracker';
  const isSubStepClickable = (index: number) => {
    return (
      allSubStepsClickable ||
      completedSubStepIndices.has(index) ||
      visitedSubStepIndices?.has(index) ||
      index <= currentSubStepIndex
    );
  };

  const handleSubStepClick = (index: number) => {
    if (onSubStepClick && isSubStepClickable(index)) {
      onSubStepClick(index);
    }
  };

  const getSubStepStatus = (index: number): ProgressTrackerSubStepStatus => {
    if (index === currentSubStepIndex) {
      return ProgressTrackerSubStepStatus.CURRENT;
    } else if (completedSubStepIndices.has(index)) {
      return ProgressTrackerSubStepStatus.COMPLETED;
    } else if (visitedSubStepIndices?.has(index)) {
      return ProgressTrackerSubStepStatus.ACCESSIBLE;
    } else if (allSubStepsClickable) {
      return ProgressTrackerSubStepStatus.ACCESSIBLE;
    } else {
      return ProgressTrackerSubStepStatus.UPCOMING;
    }
  };

  const getStatusLabel = (status: ProgressTrackerSubStepStatus) => {
    switch (status) {
      case ProgressTrackerSubStepStatus.COMPLETED:
        return 'completed';
      case ProgressTrackerSubStepStatus.CURRENT:
        return 'current';
      case ProgressTrackerSubStepStatus.ACCESSIBLE:
        return 'accessible';
      case ProgressTrackerSubStepStatus.UPCOMING:
        return 'upcoming';
      default:
        return '';
    }
  };

  return (
    <div
      aria-label='Sub-step progress'
      className={classnames('progress-tracker', className)}
      data-testid={baseTestId}
      role='navigation'>
      <div
        className='progress-tracker__icons'
        data-testid={`${baseTestId}-icons`}>
        {Array.from({ length: totalSubSteps }).map((_, index) => {
          const status = getSubStepStatus(index);
          const isClickable = isSubStepClickable(index);
          const ariaLabel = `Sub-step ${index + 1} of ${totalSubSteps}, ${getStatusLabel(status)}`;

          return (
            <div
              key={index}
              className='progress-tracker__icon-group'
              data-testid={`${baseTestId}-group-${index}`}>
              <button
                aria-label={ariaLabel}
                className={classnames('progress-tracker__icon-button', `progress-tracker__icon--${status}`)}
                data-testid={`${baseTestId}-button-${index}`}
                disabled={!isClickable}
                type='button'
                onClick={
                  isClickable
                    ? () => {
                        handleSubStepClick(index);
                      }
                    : undefined
                }>
                {status === ProgressTrackerSubStepStatus.COMPLETED && (
                  <SuccessIcon
                    ariaLabel='Completed'
                    className='progress-tracker__icon'
                    size={24}
                  />
                )}
                {status === ProgressTrackerSubStepStatus.CURRENT && (
                  <LocationIcon
                    ariaLabel='Current step'
                    className='progress-tracker__icon'
                    size={24}
                  />
                )}
                {status === ProgressTrackerSubStepStatus.UPCOMING && <span className='progress-tracker__dot'></span>}
                {status === ProgressTrackerSubStepStatus.ACCESSIBLE && (
                  <span className='progress-tracker__dot progress-tracker__dot--accessible'></span>
                )}
              </button>
              {index < totalSubSteps - 1 && (
                <span
                  className='progress-tracker__connector'
                  data-testid={`${baseTestId}-connector-${index}`}></span>
              )}
            </div>
          );
        })}
      </div>
      <div
        className='progress-tracker__count'
        data-testid={`${baseTestId}-count`}>
        ({currentSubStepIndex + 1} of {totalSubSteps})
      </div>
      <div
        className='sr-only'
        data-testid={`${baseTestId}-sr`}
        role='status'>
        Currently on sub-step {currentSubStepIndex + 1} of {totalSubSteps}. {completedSubStepIndices.size} sub-steps
        completed.
      </div>
    </div>
  );
}

export default ProgressTracker;
